import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, CheckCheck, Trash2 } from 'lucide-react';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { Tabs } from '@/components/tabs';
import { SkeletonList } from '@/components/skeleton';
import {
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/lib/notifications';
import { textFor, linkFor, formatWhen } from '@/lib/notification-display';
import { cn } from '@/lib/cn';
import type { NotificationData } from '@/types/api';

export function NotificationsPage() {
  return (
    <div className="space-y-5">
      <PageHeader icon={Bell} title="Notifications" />
      <Tabs
        paramName="tab"
        tabs={[
          { key: 'all', label: 'All', content: <NotificationList /> },
          { key: 'unread', label: 'Unread', content: <NotificationList unreadOnly /> },
        ]}
      />
    </div>
  );
}

function NotificationList({ unreadOnly = false }: { unreadOnly?: boolean }) {
  const { data: items, isLoading } = useNotifications(unreadOnly ? 'unread' : undefined);
  const markAll = useMarkAllNotificationsRead();

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <SkeletonList rows={5} />
        </CardBody>
      </Card>
    );
  }

  if (!items || items.length === 0) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            icon={BellOff}
            title={unreadOnly ? 'No unread notifications' : 'No notifications yet'}
            description={
              unreadOnly
                ? "You're all caught up. New events will show up here as they happen."
                : 'Approval signatures, payments, and client activity will show up here.'
            }
          />
        </CardBody>
      </Card>
    );
  }

  const unreadCount = items.filter((n) => !n.read_at).length;

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">
            {items.length} {items.length === 1 ? 'notification' : 'notifications'}
          </h3>
          {unreadCount > 0 ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {unreadCount} unread
            </p>
          ) : null}
        </div>
        {unreadCount > 0 ? (
          <Button variant="secondary" onClick={() => markAll.mutate()}>
            <CheckCheck className="size-3.5" /> Mark all read
          </Button>
        ) : null}
      </CardHeader>
      <CardBody>
        <ul className="divide-y divide-border/60">
          {items.map((n) => (
            <Row key={n.id} notification={n} />
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function Row({ notification }: { notification: NotificationData }) {
  const markRead = useMarkNotificationRead();
  const remove = useDeleteNotification();
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);
  const { title, body } = textFor(notification);
  const link = linkFor(notification);
  const unread = !notification.read_at;

  function onClick() {
    if (unread) markRead.mutate(notification.id);
    if (link) navigate(link);
  }

  return (
    <li
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cn(
        'group/n flex cursor-pointer items-start gap-3 px-2 py-3 transition-colors hover:bg-muted/40',
        unread && 'bg-primary/[0.03]',
      )}
    >
      <span
        className={cn(
          'mt-1.5 size-2 shrink-0 rounded-full',
          unread ? 'bg-primary' : 'bg-transparent ring-1 ring-border',
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className={cn('truncate text-sm', unread ? 'font-semibold' : 'font-medium')}>
            {title}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatWhen(notification.created_at)}
          </span>
        </div>
        {body ? (
          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{body}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          remove.mutate(notification.id);
        }}
        aria-label="Delete"
        className={cn(
          'shrink-0 rounded p-1.5 text-muted-foreground transition-opacity hover:bg-muted hover:text-destructive',
          hover ? 'opacity-100' : 'opacity-0',
        )}
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  );
}
